import React, { useEffect, useState } from "react";
import { Fingerprint, Plus, Trash2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "./ui";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

/** base64url <-> ArrayBuffer, the encoding WebAuthn uses on the wire. */
const b64ToBuf = (s) => {
  const pad = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(pad + "=".repeat((4 - (pad.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};
const bufToB64 = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export const passkeysSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential;

/**
 * Passkey management. Kept alongside TOTP rather than replacing it — passkeys are
 * device-bound, and someone who loses their only device must not lose the account.
 */
export default function PasskeySection() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.passkeys().then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!passkeysSupported()) { toast.error(t("passkeys.unsupported")); return; }
    setBusy(true);
    try {
      const { data } = await api.passkeyRegisterOptions();
      const opts = JSON.parse(data.options);
      // The browser needs real binary here, not the base64url the server sends.
      opts.challenge = b64ToBuf(opts.challenge);
      opts.user.id = b64ToBuf(opts.user.id);
      (opts.excludeCredentials || []).forEach((c) => { c.id = b64ToBuf(c.id); });

      const cred = await navigator.credentials.create({ publicKey: opts });
      await api.passkeyRegisterVerify({
        label: navigator.platform || "Passkey",
        credential: {
          id: cred.id,
          rawId: bufToB64(cred.rawId),
          type: cred.type,
          response: {
            clientDataJSON: bufToB64(cred.response.clientDataJSON),
            attestationObject: bufToB64(cred.response.attestationObject),
          },
        },
      });
      toast.success(t("passkeys.added"));
      load();
    } catch (e) {
      // A user cancelling the OS prompt is not an error worth shouting about.
      if (e?.name === "NotAllowedError") return;
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("passkeys.removeConfirm"))) return;
    try { await api.deletePasskey(id); load(); } catch { toast.error(t("dialogs.failed")); }
  };

  return (
    <Panel className="card p-5 sm:p-6" testid="passkey-section">
      <div className="flex items-center gap-2 mb-1">
        <Fingerprint size={18} className="text-brand-500" />
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("passkeys.title")}</h2>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{t("passkeys.body")}</p>

      {keys.length > 0 && (
        <div className="mt-4 divide-y divide-slate-50">
          {keys.map((k) => (
            <div key={k.id} className="py-3 flex items-center gap-3" data-testid={`passkey-${k.id}`}>
              <Fingerprint size={16} className="text-slate-300 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{k.label}</div>
                <div className="text-xs text-slate-400">
                  {t("passkeys.added_on", { date: shortDate(k.created_at) })}
                  {k.last_used_at && ` · ${t("passkeys.lastUsed", { date: shortDate(k.last_used_at) })}`}
                </div>
              </div>
              <button onClick={() => remove(k.id)} data-testid={`remove-passkey-${k.id}`}
                      className="p-2 rounded-lg text-slate-300 hover:text-rose-500 transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button className="btn-outline mt-4" onClick={register} disabled={busy} data-testid="add-passkey">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t("passkeys.add")}
      </button>

      {!passkeysSupported() && (
        <p className="text-xs text-amber-600 mt-3 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" /> {t("passkeys.unsupported")}
        </p>
      )}
      <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5">
        <Info size={12} className="mt-0.5 shrink-0" /> {t("passkeys.keepTotp")}
      </p>
    </Panel>
  );
}

/** Sign-in with a passkey, used from the login page. */
export async function signInWithPasskey(email) {
  const { data } = await api.passkeyLoginOptions(email || "");
  const opts = JSON.parse(data.options);
  opts.challenge = b64ToBuf(opts.challenge);
  (opts.allowCredentials || []).forEach((c) => { c.id = b64ToBuf(c.id); });

  const cred = await navigator.credentials.get({ publicKey: opts });
  const { data: result } = await api.passkeyLoginVerify({
    email: email || "",
    credential: {
      id: cred.id,
      rawId: bufToB64(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64(cred.response.clientDataJSON),
        authenticatorData: bufToB64(cred.response.authenticatorData),
        signature: bufToB64(cred.response.signature),
        userHandle: cred.response.userHandle ? bufToB64(cred.response.userHandle) : null,
      },
    },
  });
  return result;
}
